
import axios from 'axios';
import config from '../config/config.json';

/**
 * Servizio MCP per verificare lo status del backend FeathersJS
 * Questo servizio fornisce metodi per controllare la salute del sistema
 */
class HealthService {
  constructor() {
    this.baseUrl = config.production.host;
    this.healthEndpoint = '/health';
  }

  /**
   * Verifica lo status completo del backend
   * @returns {Promise<Object>} Status completo del sistema
   */
  async checkStatus() {
    try {
      const response = await axios.get(`${this.baseUrl}${this.healthEndpoint}`, {
        timeout: 5000 // 5 secondi di timeout
      });

      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        details: error.response?.data || null,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Verifica solo lo status del database MySQL
   * @returns {Promise<Object>} Status del database
   */
  async checkDatabase() {
    try {
      const response = await axios.get(`${this.baseUrl}${this.healthEndpoint}/database`, {
        timeout: 5000
      });

      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        details: error.response?.data || null,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Verifica lo status di FeathersJS
   * @returns {Promise<Object>} Status di FeathersJS
   */
  async checkFeathersJS() {
    try {
      const response = await axios.get(`${this.baseUrl}${this.healthEndpoint}/feathersjs`, {
        timeout: 5000
      });

      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        details: error.response?.data || null,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Verifica l'utilizzo della memoria
   * @returns {Promise<Object>} Informazioni sulla memoria
   */
  async checkMemory() {
    try {
      const response = await axios.get(`${this.baseUrl}${this.healthEndpoint}/memory`, {
        timeout: 5000
      });

      return {
        success: true,
        data: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        details: error.response?.data || null,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Verifica se il sistema è healthy (database connesso)
   * @returns {Promise<boolean>} true se il sistema è healthy
   */
  async isHealthy() {
    try {
      const status = await this.checkStatus();
      return status.success && status.data?.overall === 'healthy';
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica se MySQL è connesso
   * @returns {Promise<boolean>} true se MySQL è connesso
   */
  async isMySQLConnected() {
    try {
      const dbStatus = await this.checkDatabase();
      return dbStatus.success && dbStatus.data?.connected === true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Ottiene un report formattato dello status
   * @returns {Promise<string>} Report testuale dello status
   */
  async getStatusReport() {
    const status = await this.checkStatus();

    if (!status.success) {
      return `❌ ERRORE: Impossibile connettersi al backend\n${status.error}`;
    }

    const data = status.data;
    let report = `📊 BeSocial Backend Status Report\n`;
    report += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    report += `⏰ Timestamp: ${data.timestamp}\n`;
    report += `⚡ Uptime: ${Math.floor(data.uptime)} secondi\n`;
    report += `\n`;
    report += `🔧 FeathersJS: ${data.feathersjs.status === 'ok' ? '✅' : '❌'} (v${data.feathersjs.version})\n`;
    report += `\n`;
    report += `🗄️ Database MySQL:\n`;
    report += `   Status: ${data.database.connected ? '✅ Connesso' : '❌ Disconnesso'}\n`;

    if (data.database.version) {
      report += `   Versione: ${data.database.version}\n`;
    }
    if (data.database.name) {
      report += `   Database: ${data.database.name}\n`;
    }
    if (data.database.error) {
      report += `   Errore: ${data.database.error}\n`;
    }

    report += `\n`;
    report += `💾 Memoria:\n`;
    report += `   Usata: ${data.memory.used} ${data.memory.unit}\n`;
    report += `   Totale: ${data.memory.total} ${data.memory.unit}\n`;
    report += `\n`;
    report += `🖥️ Node.js: ${data.node.version} (${data.node.platform})\n`;
    report += `\n`;
    report += `📈 Status Generale: ${data.overall === 'healthy' ? '✅ HEALTHY' : '⚠️ UNHEALTHY'}\n`;
    report += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;

    return report;
  }

  /**
   * Monitora lo status con polling
   * @param {Function} callback - Funzione chiamata ad ogni check
   * @param {number} interval - Intervallo in millisecondi (default: 30000 = 30 secondi)
   * @returns {Function} Funzione per fermare il monitoraggio
   */
  startMonitoring(callback, interval = 30000) {
    const intervalId = setInterval(async () => {
      const status = await this.checkStatus();
      callback(status);
    }, interval);

    // Esegui subito il primo check
    this.checkStatus().then(callback);

    // Ritorna funzione per fermare il monitoraggio
    return () => clearInterval(intervalId);
  }
}

// Esporta un'istanza singleton
const healthService = new HealthService();
export default healthService;
